/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {vec2, vec3, vec4, mat4} from "gl-matrix";
import {FLOAT_PRECISION} from "App/math";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import {project} from "App/utils/glu";
import View from "App/view/view";

export default
class RayIntersector {
    /***
     * Line trinagle interection (inspred by glm::gtx::intersect::intersectLineTriangle function)
     * I found that the glm guys were inspired by Fast Minimum Storage ray Triangle Intersection by Moller and Trumbore :)
     * (http://www.cs.virginia.edu/~gfx/Courses/2003/ImageSynthesis/papers/Acceleration/Fast%20MinimumStorage%20RayTriangle%20Intersection.pdf)
     * @param aOrigin line origin
     * @param aDirection line direction
     * @param aV1 triangle vertex 1
     * @param aV2 triangle vertex 2
     * @param aV3 triangle vertex 3
     * @param rPosition returned position of type vec3
     * @returns {boolean} true if intersection was found otherwise false
     * @private
     */
    public static intersectLineTriangle(aOrigin: vec3, aDirection: vec3, aV0: vec3, aV1: vec3, aV2: vec3, rPosition: vec3): boolean {
        let Epsilon = FLOAT_PRECISION;

        let edge1 = vec3.create();
        let edge2 = vec3.create();

        vec3.subtract(edge1, aV1, aV0);
        vec3.subtract(edge2, aV2, aV0);

        let pvec = vec3.create();
        vec3.cross(pvec, aDirection, edge2);

        let det = vec3.dot(edge1, pvec);

        if (det > -Epsilon && det < Epsilon) {
            return false;
        }

        let invDet = 1.0 / det;

        let tvec = vec3.create();
        vec3.subtract(tvec, aOrigin, aV0);

        rPosition[1] = vec3.dot(tvec, pvec) * invDet;
        if (rPosition[1] < 0.0 || rPosition[1] > 1.0) {
            return false;
        }

        let qvec = vec3.create();
        vec3.cross(qvec, tvec, edge1);
        rPosition[2] = vec3.dot(aDirection, qvec) * invDet;
        if (rPosition[2] < 0.0 || (rPosition[1] + rPosition[2]) > 1.0) {
            return false;
        }

        rPosition[0] = vec3.dot(edge2, qvec) * invDet;

        // the rPosition contains (t, u, v) tupple, where the t is disntace of the plane in which the triangle lies,
        // u and w are barycentric coordinates inside the trinagle

        // Point T(u,v) on triangle is defined as:
        // T(u, v) = (1 - u - v)V0 + uV1 + vV2

        vec3.scale(aV0, aV0, 1 - rPosition[1] - rPosition[2]);
        vec3.scale(aV1, aV1, rPosition[1]);
        vec3.scale(aV2, aV2, rPosition[2]);

        vec3.add(aV0, aV0, aV1);
        vec3.add(aV0, aV0, aV2);

        rPosition[0] = aV0[0];
        rPosition[1] = aV0[1];
        rPosition[2] = aV0[2];

        return true;
    }

    /***
     * @param aOrigin line origin
     * @param aDirection line direction
     * @param aV1 line vertex 1
     * @param aV2 line vertex 2
     * @param rPosition returned position of type vec3
     * @returns {boolean} true if intersection was found otherwise false
     * @private
     */
    public static intersectLineLine(aOrigin: vec3, aDirection: vec3, aV0: vec3, aV1: vec3, rPosition: vec3): boolean {
        // TODO: finish this function
        aOrigin = aOrigin;
        aDirection = aDirection;
        aV0 = aV0;
        aV1 = aV1;
        rPosition = rPosition;

        return false;
        /*
         var Epsilon = FLOAT_PRECISION;

         var edge1 = vec3.create();
         var edge2 = vec3.create();

         vec3.subtract(edge1, aV1, aV0);
         vec3.subtract(edge2, aV2, aV0);

         var pvec = vec3.create();
         vec3.cross(pvec, aDirection, edge2);

         var det = vec3.dot(edge1, pvec);

         if (det > -Epsilon && det < Epsilon) {
         return false;
         }

         var invDet = 1.0 / det;

         var tvec = vec3.create();
         vec3.subtract(tvec, aOrigin, aV0);

         rPosition[1] = vec3.dot(tvec, pvec) * invDet;
         if (rPosition[1] < 0.0 || rPosition[1] > 1.0) {
         return false;
         }

         var qvec = vec3.create();
         vec3.cross(qvec, tvec, edge1);
         rPosition[2] = vec3.dot(aDirection, qvec) * invDet;
         if (rPosition[2] < 0.0 || (rPosition[1] + rPosition[2]) > 1.0) {
         return false;
         }

         rPosition[0] = vec3.dot(edge2, qvec) * invDet;

         // the rPosition contains (t, u, v) tupple, where the t is disntace of the plane in which the triangle lies,
         // u and w are barycentric coordinates inside the trinagle

         // Point T(u,v) on triangle is defined as:
         // T(u, v) = (1 - u - v)V0 + uV1 + vV2
         var outpos = vec3.create();

         vec3.scale(aV0, aV0, 1 - rPosition[1] - rPosition[2]);
         vec3.scale(aV1, aV1, rPosition[1]);
         vec3.scale(aV2, aV2, rPosition[2]);

         vec3.add(aV0, aV0, aV1);
         vec3.add(aV0, aV0, aV2);

         rPosition[0] = aV0[0];
         rPosition[1] = aV0[1];
         rPosition[2] = aV0[2];

         return true;*/
    }

    public static getIntersectionPoint(aGeometry: AbstractGeometry, aManipulatorTrafo: mat4, aPrimitiveSize: number, aPrimitiveIndex: number, aLinePointA: vec3, aLinePointB: vec3): vec3 | null {
        // the aPrimitiveIndex is directly index to _indices array
        let primitiveVertices = [];
        let vertexIndex = 0;
        let i;
        let intersectionPoint = vec3.create();
        let vertex;
        let lineDirection = vec3.create();
        let found = false;

        // get primitive vertices
        if (aPrimitiveSize === 1) {
            // TODO: fix point picking as it does not work because the aPrimitiveIndex is always 0 which means that no primitive point was picked which should never happen at this place
            // for points we have any indices defined
            for (i = 0; i < aPrimitiveSize; i++) {
                vertex = vec3.fromValues(
                    aGeometry.positionsDesc.data[0],
                    aGeometry.positionsDesc.data[1],
                    aGeometry.positionsDesc.data[2]
                );

                if (aManipulatorTrafo) {
                    vec3.transformMat4(vertex, vertex, aManipulatorTrafo);
                }

                primitiveVertices.push(vertex);
            }
        } else {
            for (i = 0; i < aPrimitiveSize; i++) {
                let index = ((aPrimitiveIndex - 1) * aPrimitiveSize) + i;
                if (index < 0 || index >= aGeometry.indicesDesc.data.length) {
                    return null;
                }

                vertexIndex = aGeometry.indicesDesc.data[index] * 3;
                vertex = vec3.fromValues(
                    aGeometry.positionsDesc.data[vertexIndex + 0],
                    aGeometry.positionsDesc.data[vertexIndex + 1],
                    aGeometry.positionsDesc.data[vertexIndex + 2]
                );

                if (aManipulatorTrafo) {
                    vec3.transformMat4(vertex, vertex, aManipulatorTrafo);
                }

                primitiveVertices.push(vertex);
            }
        }

        // we have point or line
        if (aPrimitiveSize === 1) {
            // for vertices, the intersection point is the point itself
            return primitiveVertices[0];
        } else if (aPrimitiveSize === 2) {
            // lines
            vec3.subtract(lineDirection, aLinePointB, aLinePointA);
            vec3.normalize(lineDirection, lineDirection);

            // plane line intersection
            found = RayIntersector.intersectLineLine(
                aLinePointA,
                lineDirection,
                primitiveVertices[0],
                primitiveVertices[1],
                intersectionPoint
            );

            if (found) {
                return intersectionPoint;
            }

            return null;
        } else if (aPrimitiveSize === 3) {
            // triangles
            vec3.subtract(lineDirection, aLinePointB, aLinePointA);
            vec3.normalize(lineDirection, lineDirection);

            // plane line intersection
            found = RayIntersector.intersectLineTriangle(
                aLinePointA,
                lineDirection,
                primitiveVertices[0],
                primitiveVertices[1],
                primitiveVertices[2],
                intersectionPoint
            );

            if (found) {
                return intersectionPoint;
            }

            return null;
        }
    }

    public static getClosestPoint(aGeometry: AbstractGeometry, aManipulatorTrafo: mat4, aPrimitiveSize: number, aPrimitiveIndex: number, aView: View, aX: number, aY: number): {position: vec3, index: number} {
        // the aPrimitiveIndex is directly index to _indices array
        let primitiveVertices = [];
        let vertexIndex = 0;

        let vertex;

        if (aPrimitiveSize === 1) {
            // for points we have any indices defined
            for (let i = 0; i < aPrimitiveSize; i++) {
                vertex = vec3.fromValues(
                    aGeometry.positionsDesc.data[0],
                    aGeometry.positionsDesc.data[1],
                    aGeometry.positionsDesc.data[2]
                );

                if (aManipulatorTrafo) {
                    vec3.transformMat4(vertex, vertex, aManipulatorTrafo);
                }

                primitiveVertices.push(vertex);
            }
        } else {
            for (let i = 0; i < aPrimitiveSize; i++) {
                vertexIndex = aGeometry.indicesDesc.data[((aPrimitiveIndex - 1) * aPrimitiveSize) + i] * 3;
                vertex = vec3.fromValues(
                    aGeometry.positionsDesc.data[vertexIndex + 0],
                    aGeometry.positionsDesc.data[vertexIndex + 1],
                    aGeometry.positionsDesc.data[vertexIndex + 2]);

                if (aManipulatorTrafo) {
                    vec3.transformMat4(vertex, vertex, aManipulatorTrafo);
                }

                primitiveVertices.push(vertex);
            }
        }

        // project the primitiveVertices to screen
        let projectedPoints = [];
        for (let i = 0; i < primitiveVertices.length; i++) {
            let newWindowPoint: vec4 = vec4.create();
            let position = aView.getPosition();
            let size = aView.getSize();
            project(
                newWindowPoint,
                primitiveVertices[i],
                aView.getCamera().getViewProjectionMatrix(),
                [position[0], position[1], size[0], size[1]]
            );
            projectedPoints.push(newWindowPoint);
        }

        // compare distances of the projected points with mouse position [aX, aY]
        let distances = [];
        for (let i = 0; i < projectedPoints.length; i++) {
            distances.push({
                distance: vec2.distance(projectedPoints[i], vec2.fromValues(aX, aView.getContentSize()[1] - aY)),
                index: i
            });
        }

        distances.sort(function (a, b) {
            return a.distance - b.distance;
        });

        return {
            position: primitiveVertices[distances[0].index],
            index: distances[0].index
        }
    }
}
